import { z } from "zod";
import { businessRoleSchema, jobTitleSchema } from "../enums.ts";
import { coverRequestStatusSchema } from "./covers.ts";
import { isoDateTimeSchema, uuidSchema } from "./common.ts";

/**
 * One staff member's page (Staff, then a person; 2026-11-03). Owner and admin only. It shows the
 * person, their schedule and their activity. Their month of classes comes from the report "Classes
 * per staff member" (`reports.ts`), so the two always agree.
 */

export const staffMemberParamSchema = z.object({ businessId: uuidSchema, userId: uuidSchema });

export const staffMemberLevelSchema = z.object({
  groupId: uuidSchema,
  name: z.string(),
  /** A private (the group kind `private`). */
  private: z.boolean(),
  /** This person is the only staff member of the group: nobody else runs it when they are away. */
  onlyStaff: z.boolean(),
});

export const staffMemberResponseSchema = z.object({
  userId: uuidSchema,
  name: z.string(),
  role: businessRoleSchema,
  jobTitle: jobTitleSchema.nullable(),
  phoneE164: z.string().nullable(),
  email: z.string().nullable(),
  /** Signed, expires in 12 to 24 hours; never store it. */
  photoUrl: z.string().nullable(),
  /** When they joined the team of this business. */
  joinedAt: isoDateTimeSchema,
  /** The last time the person used a Teamna app on a phone. Null: never on a phone. */
  lastSeenAt: isoDateTimeSchema.nullable(),
  timezone: z.string(),
  levels: z.array(staffMemberLevelSchema),
});
export type StaffMemberResponse = z.infer<typeof staffMemberResponseSchema>;

// --- activity ----------------------------------------------------------------

/** A session an activity row is about. */
const activitySessionSchema = z.object({
  sessionId: uuidSchema,
  className: z.string(),
  startsAt: isoDateTimeSchema,
});

/**
 * What the person did, newest first. Each kind comes from the table that holds the record, not
 * from a copy:
 * - `attendance_marked`: one row per session they marked (bookings). `minutesAfterEnd` is the last
 *   mark minus the session end; below zero means they marked during the class.
 * - `someone_else_added`: names they added to a session ("Someone else came", session extras).
 * - `cover_asked` and `cover_taken`: cover requests they made, and covers they took.
 * - `names_added`: names they typed for a group on one day (coaches add their participants).
 * - `action`: an audit log row with a known action (`staffActivityActions`).
 */
export const staffActivityActions = [
  "participant.placed",
  "family.child_added",
  "family.adult_added",
  "family.linked",
  "pending_participant.linked",
  "pending_participant.removed",
  "parent_claim.linked",
  "credits.adjusted",
  "package.sold",
  "payment_request.created",
  "group.created",
  "timetable.month_published",
  "profile_photo.set",
] as const;
export const staffActivityActionSchema = z.enum(staffActivityActions);
export type StaffActivityAction = z.infer<typeof staffActivityActionSchema>;

export const staffActivityItemSchema = z.discriminatedUnion("kind", [
  z.object({
    kind: z.literal("attendance_marked"),
    at: isoDateTimeSchema,
    session: activitySessionSchema,
    present: z.number().int().min(0),
    absent: z.number().int().min(0),
    minutesAfterEnd: z.number().int(),
  }),
  z.object({ kind: z.literal("someone_else_added"), at: isoDateTimeSchema, session: activitySessionSchema, count: z.number().int().min(1) }),
  z.object({
    kind: z.literal("cover_asked"),
    at: isoDateTimeSchema,
    session: activitySessionSchema,
    status: coverRequestStatusSchema,
    /** Who took it. Null while nobody did. */
    takenByName: z.string().nullable(),
  }),
  z.object({
    kind: z.literal("cover_taken"),
    at: isoDateTimeSchema,
    session: activitySessionSchema,
    /** Who asked for the cover. */
    fromName: z.string().nullable(),
  }),
  z.object({ kind: z.literal("names_added"), at: isoDateTimeSchema, groupName: z.string(), count: z.number().int().min(1) }),
  z.object({ kind: z.literal("action"), at: isoDateTimeSchema, action: staffActivityActionSchema }),
]);
export type StaffActivityItem = z.infer<typeof staffActivityItemSchema>;

/**
 * The activity page cursor: `<ISO time to the millisecond>|<source>:<row key>`, the place of the
 * last row of the page before. Opaque to the client: it only sends back `nextBefore`.
 */
export const staffActivityCursorSchema = z
  .string()
  .regex(/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}\.\d{3}Z\|[1-6]:[0-9A-Za-z:-]{1,80}$/, "Not a page cursor.");

export const staffActivityQuerySchema = z.object({
  /** The rows after this place in the list (older). Missing: the first page. */
  before: staffActivityCursorSchema.optional(),
});

export const staffActivityResponseSchema = z.object({
  items: z.array(staffActivityItemSchema),
  /** Pass as `before` for the next page. Null: there is nothing older. */
  nextBefore: staffActivityCursorSchema.nullable(),
});
export type StaffActivityResponse = z.infer<typeof staffActivityResponseSchema>;
